import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { useI18n } from '@/hooks/useI18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    ArrowLeftIcon,
    UsersIcon,
    BriefcaseIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import { apiFetch } from '@/utils/api';

interface SyncResult {
    success: boolean;
    created?: number;
    updated?: number;
    skipped?: number;
    errors?: string[];
    message?: string;
}

export default function ConnecteamIntegrationPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const { t } = useI18n();

    const [syncing, setSyncing] = useState<string | null>(null);
    const [syncResults, setSyncResults] = useState<Record<string, SyncResult>>({});

    // Shifts sync date range (optional)
    const [shiftFromDate, setShiftFromDate] = useState<string>(""); // YYYY-MM-DD
    const [shiftToDate, setShiftToDate] = useState<string>("");     // YYYY-MM-DD

    const handleSync = async (syncType: 'users' | 'jobs' | 'time-activities' | 'shifts') => {
        setSyncing(syncType);
        setSyncResults(prev => ({ ...prev, [syncType]: { success: false, message: 'Syncing...' } }));

        try {
            let endpoint = `/api/connecteam/sync-${syncType}`;
            // Append optional date range for shifts
            if (syncType === 'shifts') {
                const params = new URLSearchParams();
                if (shiftFromDate) params.set('from_date', new Date(shiftFromDate).toISOString());
                if (shiftToDate) params.set('to_date', new Date(shiftToDate).toISOString());
                const qs = params.toString();
                if (qs) endpoint += `?${qs}`;
            }
            const response = await apiFetch(endpoint, {
                method: 'POST'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            setSyncResults(prev => ({ ...prev, [syncType]: result }));
        } catch (error: any) {
            console.error(`Failed to sync ${syncType}:`, error);
            setSyncResults(prev => ({
                ...prev,
                [syncType]: {
                    success: false,
                    errors: [error.message || `Failed to sync ${syncType}`]
                }
            }));
        } finally {
            setSyncing(null);
        }
    };
    const handleSyncShiftsAndUnavailabilities = async () => {
        setSyncing('combined');
        setSyncResults(prev => ({ ...prev, combined: { success: false, message: 'Syncing...' } }));
        try {
            const params = new URLSearchParams();
            if (shiftFromDate) params.set('from_date', new Date(shiftFromDate).toISOString());
            if (shiftToDate) params.set('to_date', new Date(shiftToDate).toISOString());
            const qs = params.toString();

            const shiftsEndpoint = `/api/connecteam/sync-shifts${qs ? `?${qs}` : ''}`;
            const timeActivitiesEndpoint = `/api/connecteam/sync-time-activities${qs ? `?${qs}` : ''}`;
            const unavailabilityEndpoint = `/api/connecteam/sync-unavailabilities${qs ? `?${qs}` : ''}`;

            // Run all three in parallel for efficiency
            const [shiftsRes, taRes, unavaRes] = await Promise.all([
                apiFetch(shiftsEndpoint, { method: 'POST' }),
                apiFetch(timeActivitiesEndpoint, { method: 'POST' }),
                apiFetch(unavailabilityEndpoint, { method: 'POST' }),
            ]);

            if (!shiftsRes.ok) throw new Error(`Shifts sync failed: ${shiftsRes.status}`);
            if (!taRes.ok) throw new Error(`Time activities sync failed: ${taRes.status}`);
            if (!unavaRes.ok) throw new Error(`Unavailabilities sync failed: ${unavaRes.status}`);

            const shiftsJson = await shiftsRes.json();
            const taJson = await taRes.json();
            const unavaJson = await unavaRes.json();

            const combined = {
                success: Boolean(shiftsJson.success) && Boolean(taJson.success) && Boolean(unavaJson.success),
                created: (shiftsJson.created || 0) + (taJson.created || 0) + (unavaJson.created || 0),
                updated: (shiftsJson.updated || 0) + (taJson.updated || 0) + (unavaJson.updated || 0),
                skipped: (shiftsJson.skipped || 0) + (taJson.skipped || 0) + (unavaJson.skipped || 0),
                message: 'Shifts + Time Offs + Unavailabilities sync completed',
                errors: [ ...(shiftsJson.errors || []), ...(taJson.errors || []), ...(unavaJson.errors || []) ],
            } as SyncResult;

            setSyncResults(prev => ({
                ...prev,
                combined,
                shifts: shiftsJson,
                ['time-activities']: taJson,
                unavailabilities: unavaJson,
            }));
        } catch (error: any) {
            console.error('Combined sync failed:', error);
            setSyncResults(prev => ({
                ...prev,
                combined: { success: false, errors: [error.message || 'Combined sync failed'] }
            }));
        } finally {
            setSyncing(null);
        }
    };


    const renderSyncResult = (syncType: string) => {
        const result = syncResults[syncType];
        if (!result) return null;

        return (
            <Alert className={result.success ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
                <div className="flex items-start gap-2">
                    {result.success ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                    ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                        <AlertDescription>
                            {result.message && <p className="font-medium mb-1">{result.message}</p>}
                            {result.success && (
                                <div className="text-sm space-y-1">
                                    {result.created !== undefined && <p>Created: {result.created}</p>}
                                    {result.updated !== undefined && <p>Updated: {result.updated}</p>}
                                    {result.skipped !== undefined && <p>Skipped: {result.skipped}</p>}
                                </div>
                            )}
                            {result.errors && result.errors.length > 0 && (
                                <div className="text-sm space-y-1 mt-2">
                                    <p className="font-medium">Errors:</p>
                                    {result.errors.map((error, idx) => (
                                        <p key={idx} className="text-red-700">• {error}</p>
                                    ))}
                                </div>
                            )}
                        </AlertDescription>
                    </div>
                </div>
            </Alert>
        );
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <Button
                        variant="ghost"
                        onClick={() => navigate({ to: '/admin/integrations'})}
                        className="mb-4"
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back to Integrations
                    </Button>
                    <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                        <UsersIcon className="h-8 w-8" />
                        Connecteam Integration
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Sync employees, jobs, and time tracking data from Connecteam
                    </p>
                </div>

                {/* Sync Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Sync Employees */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <UsersIcon className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <CardTitle>Employees</CardTitle>
                                    <CardDescription>Sync employee data</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Import employee information including names, emails, and contact details from Connecteam.
                            </p>
                            <Button
                                onClick={() => handleSync('users')}
                                disabled={syncing !== null}
                                className="w-full"
                            >
                                {syncing === 'users' ? (
                                    <>
                                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                        Syncing...
                                    </>
                                ) : (
                                    'Sync Employees'
                                )}
                            </Button>
                            {renderSyncResult('users')}
                        </CardContent>
                    </Card>

                    {/* Sync Jobs */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <BriefcaseIcon className="h-6 w-6 text-green-600" />
                                </div>
                                <div>
                                    <CardTitle>Jobs</CardTitle>
                                    <CardDescription>Sync job positions</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Import job titles and positions from Connecteam for time tracking categorization.
                            </p>
                            <Button
                                onClick={() => handleSync('jobs')}
                                disabled={syncing !== null}
                                className="w-full"
                            >
                                {syncing === 'jobs' ? (
                                    <>
                                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                        Syncing...
                                    </>
                                ) : (
                                    'Sync Jobs'
                                )}
                            </Button>
                            {renderSyncResult('jobs')}
                        </CardContent>
                    </Card>

                    {/* Sync Time Activities */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-orange-100 rounded-lg">
                                    <ClockIcon className="h-6 w-6 text-orange-600" />
                                </div>
                                <div>
                                    <CardTitle>Time Activities</CardTitle>
                                    <CardDescription>Sync time tracking</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Import shifts, breaks, and time off records from Connecteam for payroll processing.
                            </p>
                            <Button
                                onClick={() => handleSync('time-activities')}
                                disabled={syncing !== null}
                                className="w-full"
                            >
                                {syncing === 'time-activities' ? (
                                    <>
                                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                        Syncing...
                                    </>
                                ) : (
                                    'Sync Time Activities'
                                )}
                            </Button>
                            {renderSyncResult('time-activities')}
                        </CardContent>
                    </Card>

                    {/* Sync Shifts */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">

                                    <ClockIcon className="h-6 w-6 text-purple-600" />
                                </div>
                                <div>
                                    <CardTitle>Shifts</CardTitle>
                                    <CardDescription>Sync scheduled shifts</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Fetch scheduled shifts from Connecteam into CafeMagic's timesheet shifts database.
                            </p>

                            {/* Date range selectors (optional) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-muted-foreground mb-1">From date</label>
                                    <input
                                        type="date"
                                        className="w-full border rounded-md p-2 text-sm"
                                        value={shiftFromDate}
                                        onChange={(e) => setShiftFromDate(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-muted-foreground mb-1">To date</label>
                                    <input
                                        type="date"
                                        className="w-full border rounded-md p-2 text-sm"
                                        value={shiftToDate}
                                        onChange={(e) => setShiftToDate(e.target.value)}
                                    />
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground">Leave blank to use the default 30-day window.</p>

                            <Button
                                onClick={() => handleSync('shifts')}
                                disabled={syncing !== null}
                                className="w-full mt-2"
                            >
                                {syncing === 'shifts' ? (
                                    <>
                                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                        Syncing...
                                    </>
                                ) : (
                                    'Sync Shifts'
                                )}
                            </Button>
                            {renderSyncResult('shifts')}

                            <Button
                                onClick={handleSyncShiftsAndUnavailabilities}
                                disabled={syncing !== null}
                                variant="secondary"
                                className="w-full mt-2"
                            >
                                {syncing === 'combined' ? (
                                    <>
                                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                        Syncing all...
                                    </>
                                ) : (
                                    'Sync Shifts + Time Offs + Unavailabilities'
                                )}
                            </Button>
                            {renderSyncResult('combined')}
                        </CardContent>
                    </Card>
                </div>

                {/* Initial Setup Instructions */}
                <Card className="bg-blue-50 border-blue-200">
                    <CardHeader>
                        <CardTitle className="text-lg">Initial Setup</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <p className="font-medium">For first-time setup, follow these steps in order:</p>
                        <ol className="list-decimal list-inside space-y-2 ml-2">
                            <li>Click <strong>"Sync Employees"</strong> to import all employee records</li>
                            <li>Click <strong>"Sync Jobs"</strong> to import job positions and titles</li>
                            <li>Click <strong>"Sync Time Activities"</strong> to import time tracking data</li>
                        </ol>
                        <p className="text-muted-foreground mt-4">

                            After initial setup, you can run these syncs periodically to keep data up to date.
                            Time Activities sync is incremental and will only fetch new data since the last sync.
                        </p>
                    </CardContent>
                </Card>

                {/* Configuration Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Configuration</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm text-muted-foreground">
                        <p>
                            Connecteam integration is configured via environment variables on the server.
                            Contact your system administrator if you need to update API credentials or settings.
                        </p>
                        <div className="mt-4 p-3 bg-muted rounded-lg">
                            <p className="font-medium text-foreground mb-2">Required Environment Variables:</p>
                            <ul className="space-y-1 font-mono text-xs">
                                <li>• CONNECTEAM_API_BASE</li>
                                <li>• CONNECTEAM_API_KEY</li>
                                <li>• CONNECTEAM_COMPANY_ID</li>
                                <li>• CONNECTEAM_TIMECLOCK_ID</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/admin/integrations/connecteam')({
  component: ConnecteamIntegrationPage,
})
